import {
  usePublicBoundlessConfigRetrieve,
  usePublicPartnerRetrieve,
} from "@boundless/bl-api-client/public/services"
import { Link, linkOptions } from "@tanstack/react-router"
import { CalendarIcon, UserPlusIcon } from "lucide-react"

import { FundingRangeBar } from "@/components/fundready/FundingRangeBar"
import { FundReadyHeader } from "@/components/fundready/FundReadyHeader"
import { Gauge } from "@/components/fundready/Gauge"
import { SectionHeading } from "@/components/fundready/SectionHeading"
import { Button } from "@/components/ui/button"
import { FieldInfo } from "@/components/ui/field-info"
import { Progress } from "@/components/ui/progress"
import {
  BAND_COLOR,
  BAND_LABEL,
  BAND_TEXT_CLASS,
  CASH_FLOW_BADGE,
  CASH_FLOW_COPY,
  fmt$,
  fmtPct,
  LENDER_STATUS_LABEL,
  loadStyle,
  pluralize,
  PRODUCT_LABEL,
  SCORE_ROWS,
} from "@/lib/fundready/report-content"
import BoundlessLogo from "@/svg/boundless/logo.svg?react"

import type { FundReadyReport } from "@boundless/bl-api-client/public/models/FundReadyReport"
import type { ProductLenderCount } from "@boundless/bl-api-client/public/models/ProductLenderCount"

type ReportStepProps = {
  askAmount: number
  report: FundReadyReport
  lenderCounts: ProductLenderCount[]
  partnerSlug?: string
  onEditAnswers: () => void
  onStartOver: () => void
}

export function ReportStep({
  askAmount,
  report,
  lenderCounts,
  partnerSlug,
  onEditAnswers,
  onStartOver,
}: ReportStepProps) {
  const { data: config } = usePublicBoundlessConfigRetrieve()
  // The partner signup route 404s on a slug it can't resolve, so check first
  // and fall back to plain signup rather than dead-ending the highest-intent
  // click on the page.
  const { data: partner, isPending: isPartnerPending } =
    usePublicPartnerRetrieve(partnerSlug ?? "", {
      query: { enabled: !!partnerSlug, retry: false },
    })
  // While the lookup is in flight `partner` is undefined, which would
  // otherwise fall back to plain signup on a fast click. Disable the
  // button until the partner is resolved so the user doesn't click through prematurely.
  const partnerResolving = !!partnerSlug && isPartnerPending
  const signupLink =
    partner && partnerSlug
      ? linkOptions({ to: "/p/$slug/signup", params: { slug: partnerSlug } })
      : linkOptions({ to: "/signup" })

  const bookACallUrl = config?.fundreadyBookACallUrl

  const bandColor = BAND_COLOR[report.band] ?? "var(--color-fr-brand-soft)"
  const totalLenders = lenderCounts.reduce((sum, l) => sum + l.count, 0)
  const eligibleProducts = lenderCounts.filter((l) => l.count > 0).length

  // Which numbered sections appear depends on the optional cost/cash-flow
  // blocks, so build the visible list once and read each section's ordinal from
  // it rather than recomputing the number inline.
  const numberedSections = [
    "expectations",
    ...(report.apr != null ? ["cost"] : []),
    ...(report.cashFlow ? ["cashFlow"] : []),
    "paths",
  ]
  const sectionNo = (key: string) =>
    String(numberedSections.indexOf(key) + 1).padStart(2, "0")

  return (
    <div className="bg-fr-page min-h-screen">
      <FundReadyHeader />
      <div className="mx-auto max-w-4xl p-8">
        <div className="border-fr-border shadow-fr rounded-[14px] border bg-white px-[34px] py-9">
          <div className="text-fr-muted mb-[18px] text-[11px] font-semibold tracking-[0.12em] uppercase">
            FundReady™ Report ·{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </div>

          <div className="flex flex-wrap items-center gap-8">
            <Gauge score={report.score} color={bandColor} />
            <div>
              <div
                className={`text-[30px] font-bold tracking-[-0.02em] ${BAND_TEXT_CLASS[report.band] ?? "text-fr-brand-soft"}`}
              >
                {BAND_LABEL[report.band] ?? report.band}
              </div>
              <p className="text-fr-muted mt-1.5 max-w-[420px] text-[14.5px] leading-[1.5]">
                {eligibleProducts > 0 ? (
                  <>
                    Based on your profile,{" "}
                    <strong className="text-fr-ink">
                      {pluralize(
                        totalLenders,
                        "a lender match",
                        "lender matches",
                      )}
                    </strong>{" "}
                    across{" "}
                    <strong className="text-fr-ink">
                      {pluralize(
                        eligibleProducts,
                        "1 funding product",
                        "funding products",
                      )}
                    </strong>{" "}
                    in the Boundless network can support your business today.
                  </>
                ) : (
                  "Your profile doesn't currently meet minimum lender criteria - the breakdown below shows exactly what to change."
                )}
              </p>
            </div>
          </div>

          <SectionHeading>How your score was calculated</SectionHeading>
          <table className="mt-2 w-full border-collapse text-[13.5px]">
            <tbody>
              {SCORE_ROWS.map((row) => (
                <tr key={row.key}>
                  <td className="border-fr-border border-b px-1 py-2">
                    {row.label}{" "}
                    <span className="text-fr-muted">- {row.note}</span>
                  </td>
                  <td className="border-fr-border border-b px-1 py-2 text-right font-semibold">
                    {report.subScores[row.key]} / {row.max}
                  </td>
                </tr>
              ))}
              <tr>
                <td className="px-1 py-2 font-semibold">FundReady™ Score</td>
                <td className="px-1 py-2 text-right text-[15px] font-semibold">
                  {report.score} / 100
                </td>
              </tr>
            </tbody>
          </table>

          <SectionHeading>
            {sectionNo("expectations")} · Funding expectations
          </SectionHeading>
          <div className="mt-2 flex flex-wrap gap-9">
            <div>
              <div className="text-fr-muted text-[11px] font-semibold tracking-[0.12em] uppercase">
                Your ask
              </div>
              <div className="text-fr-ink text-[clamp(30px,4.5vw,42px)] font-extrabold tracking-[-0.025em]">
                {fmt$(askAmount)}
              </div>
            </div>
            <div>
              <div className="text-fr-muted text-[11px] font-semibold tracking-[0.12em] uppercase">
                Unsecured capacity
              </div>
              <div className="text-fr-brand text-[clamp(30px,4.5vw,42px)] font-extrabold tracking-[-0.025em]">
                {fmt$(report.rangeLow)}–{fmt$(report.rangeHigh)}
              </div>
            </div>
            {report.assetUnlock > 0 && (
              <div>
                <div className="text-fr-muted text-[11px] font-semibold tracking-[0.12em] uppercase">
                  Total capacity
                </div>
                <div className="text-fr-brand-soft text-[clamp(30px,4.5vw,42px)] font-extrabold tracking-[-0.025em]">
                  {fmt$(report.totalCapacity)}
                </div>
              </div>
            )}
          </div>
          <FundingRangeBar report={report} askAmount={askAmount} />
          <div className="mt-[18px]">
            {report.gaps.length === 0 ? (
              <div className="border-fr-brand text-fr-muted my-2.5 border-l-[3px] py-2.5 pl-4 text-[14.5px] leading-[1.55]">
                Your ask sits inside your realistic range. Lenders will see this
                as a credible, well-sized request - that improves both approval
                odds and pricing.
              </div>
            ) : (
              report.gaps.map((g, i) => (
                <div
                  key={i}
                  className="border-fr-border text-fr-muted my-2.5 border-l-[3px] py-2.5 pl-4 text-[14.5px] leading-[1.55]"
                >
                  {g}
                </div>
              ))
            )}
          </div>

          {report.apr != null && (
            <>
              <SectionHeading>
                {sectionNo("cost")} · True cost of your offer
              </SectionHeading>
              <div className="flex flex-wrap items-end gap-9">
                <div>
                  <div className="text-fr-muted flex items-center text-[11px] font-semibold tracking-[0.12em] uppercase">
                    True APR
                    {report.aprCapped && (
                      <FieldInfo text="A + means the true rate is at least this high." />
                    )}
                  </div>
                  <div className="text-fr-ink text-[clamp(30px,4.5vw,42px)] font-extrabold tracking-[-0.025em]">
                    {fmtPct(report.apr, 0)}
                    {report.aprCapped ? "+" : ""}
                  </div>
                </div>
                <div>
                  <div className="text-fr-muted text-[11px] font-semibold tracking-[0.12em] uppercase">
                    Total cost of capital
                  </div>
                  <div className="text-fr-ink text-[clamp(30px,4.5vw,42px)] font-extrabold tracking-[-0.025em]">
                    {fmt$(report.offerCost)}
                  </div>
                </div>
                {report.competeCount > 0 && (
                  <p className="text-fr-muted max-w-[340px] text-[14.5px] leading-[1.55]">
                    Based on your profile,{" "}
                    <strong className="text-fr-ink">
                      {pluralize(report.competeCount, "a lender", "lenders")}
                    </strong>{" "}
                    could compete directly with this offer.
                  </p>
                )}
              </div>
            </>
          )}

          {report.cashFlow && (
            <>
              <SectionHeading>
                {sectionNo("cashFlow")} · Cash flow stress test
              </SectionHeading>
              <p className="text-fr-muted mt-1 mb-3.5 text-[14.5px] leading-[1.5]">
                Modeled on a {fmt$(report.cashFlow.recommendedAmount)} advance
                at typical terms (~
                {fmt$(report.cashFlow.newPaymentMonthly)}/month in payments).
                Lenders get nervous past 18% of revenue; above 25% is where
                businesses get hurt.
              </p>
              {[
                { label: "Today", v: report.cashFlow.loadNow },
                { label: "With new funding", v: report.cashFlow.loadFunded },
                {
                  label: "If revenue dips 20%",
                  v: report.cashFlow.loadStress,
                },
              ].map((row) => {
                const { bar, text } = loadStyle(row.v)
                return (
                  <div
                    key={row.label}
                    className="my-3 flex items-center gap-3.5"
                  >
                    <div className="w-[170px] shrink-0 text-[13.5px] font-medium">
                      {row.label}
                    </div>
                    <Progress
                      value={Math.min(100, row.v * 100)}
                      className="bg-fr-track h-[18px] flex-1"
                      indicatorClassName={bar}
                    />
                    <div
                      className={`w-16 text-right text-[13px] font-semibold ${text}`}
                    >
                      {fmtPct(row.v)}
                    </div>
                  </div>
                )
              })}
              <div className="mt-2.5 flex items-center gap-2.5">
                <span
                  className={`shrink-0 rounded-full px-[9px] py-[3px] text-center text-[11px] font-semibold tracking-[0.06em] whitespace-nowrap ${
                    CASH_FLOW_BADGE[report.cashFlow.verdict] ??
                    CASH_FLOW_BADGE.Comfortable
                  }`}
                >
                  {report.cashFlow.verdict.toUpperCase()}
                </span>
                <span className="text-fr-muted text-[13.5px]">
                  {CASH_FLOW_COPY[report.cashFlow.verdict] ??
                    CASH_FLOW_COPY.Comfortable}
                </span>
              </div>
            </>
          )}

          <SectionHeading>
            {sectionNo("paths")} · Funding paths your business unlocks
          </SectionHeading>
          <div className="mt-3.5 grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3">
            {lenderCounts.map((product) => {
              const matched =
                product.status === "count" || product.status === "talk_to_us"
              return (
                <div
                  key={product.product}
                  className={`rounded-xl border px-4 py-3.5 ${
                    matched
                      ? "border-fr-brand bg-fr-brand-tint"
                      : "border-fr-border bg-fr-surface"
                  }`}
                >
                  <b className="block text-[14.5px] font-bold">
                    {PRODUCT_LABEL[product.product] ?? product.product}
                  </b>
                  <div
                    className={`my-1 text-xl font-extrabold ${
                      matched ? "text-fr-brand" : "text-fr-muted"
                    }`}
                  >
                    {product.status === "count"
                      ? `${product.count} lenders`
                      : product.status === "talk_to_us"
                        ? "Talk to us"
                        : "Not yet"}
                  </div>
                  <p className="text-fr-muted text-[12.5px] leading-[1.45]">
                    {product.status === "count"
                      ? "Meets current lender criteria in the Boundless network."
                      : (LENDER_STATUS_LABEL[product.status] ??
                        LENDER_STATUS_LABEL.improve_profile)}
                  </p>
                </div>
              )
            })}
          </div>

          <div className="mt-[30px] flex flex-col gap-3 sm:flex-row">
            <div className="border-fr-brand bg-fr-brand-tint shadow-fr-card flex-1 rounded-xl border p-6">
              <UserPlusIcon className="text-fr-brand-strong mb-2 size-5" />
              <h3 className="text-fr-brand-strong mb-1.5 text-[19px] font-extrabold">
                Create your account
              </h3>
              <p className="text-fr-muted mb-4 text-[14.5px] leading-[1.55]">
                Set up your Boundless profile to connect your bank, share your
                documents, and start matching with the lenders above.
              </p>
              <Button
                type="button"
                disabled={partnerResolving}
                asChild={!partnerResolving}
              >
                {partnerResolving ? (
                  "Get Started"
                ) : (
                  <Link {...signupLink} target="_blank" rel="noreferrer">
                    Get Started
                  </Link>
                )}
              </Button>
            </div>

            {/* Only when configured: FUNDREADY_BOOK_A_CALL_URL can be blanked
                out, and a dead link is worse than a single CTA. */}
            {bookACallUrl && (
              <div className="border-fr-border shadow-fr-card flex-1 rounded-xl border bg-white p-6">
                <CalendarIcon className="text-fr-ink mb-2 size-5" />
                <h3 className="text-fr-ink mb-1.5 text-[19px] font-extrabold">
                  Talk to an advisor
                </h3>
                <p className="text-fr-muted mb-4 text-[14.5px] leading-[1.55]">
                  Prefer to talk it through first? Book a call and they&apos;ll
                  walk you through your report.
                </p>
                <Button type="button" variant="outline" asChild>
                  <a href={bookACallUrl} target="_blank" rel="noreferrer">
                    Book a call
                  </a>
                </Button>
              </div>
            )}
          </div>

          <div className="mt-[26px] flex justify-between">
            <Button type="button" variant="outline" onClick={onEditAnswers}>
              Edit my answers
            </Button>
            <Button type="button" variant="outline" onClick={onStartOver}>
              Start over
            </Button>
          </div>

          <div className="border-fr-border text-fr-muted mt-9 border-t pt-3.5 text-[11.5px] leading-[1.5]">
            Estimates are based on entered information and typical lender
            criteria in the Boundless network; they are not an offer, approval,
            or financial advice. Actual amounts and pricing depend on
            underwriting. No credit check is performed by this tool. Boundless
            is a funding marketplace, not a lender.
            <div className="mt-3.5 flex items-center justify-center gap-1.5">
              <span className="text-fr-muted text-[11px]">Powered by</span>
              <BoundlessLogo className="h-3.5 w-auto opacity-75" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
